import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';

import { FHCopyToClipboard } from '../../../base';
import { parsePhoneNumber, usDollarFormatter } from '../../../../helpers';
import Claim1500FormServiceLines from './Claim1500FormServiceLines';

import type { RootState } from '../../../../store';

interface ClaimFormInfoProps {
  showPreview?: boolean;
  fieldContainerStyle: any;
  fieldLabelStyle: any;
  fieldContentStyle: any;
}

// 1500 Form Info
function Claim1500FormInfo(props: ClaimFormInfoProps) {
  const { fieldContainerStyle, fieldLabelStyle, fieldContentStyle, showPreview = false } = props;

  const { current: claim, claimPreview } = useSelector((state: RootState) => state.claims);

  const fieldLabelIndexStyle = {
    ...fieldLabelStyle,
    width: 34,
  };

  const fieldCopyContentStyle = {
    ml: '28px',
  };

  const claimData = showPreview ? { form_1500_data: claimPreview } : claim;

  if (!claimData?.form_1500_data) return <></>;
  const { form_1500_data } = claimData;

  return (
    <>
      <Box sx={fieldContainerStyle} data-testid="f1500PayerId">
        <Typography sx={fieldLabelStyle}>Payer ID</Typography>
        <Typography sx={{ ...fieldContentStyle, ml: 1, textAlign: 'left' }}>
          {form_1500_data?.payor_id || ''}
        </Typography>
      </Box>
      <Box sx={fieldContainerStyle} data-testid="f1500Insurance">
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>1.</Typography>
          <Typography sx={fieldLabelStyle}>Insurance</Typography>
        </Box>
        <Typography sx={fieldContentStyle}>{form_1500_data?.insurance_type || ''}</Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>1a.</Typography>
          <Typography sx={fieldLabelStyle}>Insured I.D number</Typography>
        </Box>
        <FHCopyToClipboard text={form_1500_data?.member_id || ''} sx={fieldCopyContentStyle} />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>2.</Typography>
          <Typography sx={fieldLabelStyle}>
            Patient’s name (Last , First , Middle initial)
          </Typography>
        </Box>
        <FHCopyToClipboard text={form_1500_data?.patient?.name || ''} sx={fieldCopyContentStyle} />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>3.</Typography>
          <Typography sx={fieldLabelStyle}>Patients birthdate and sex</Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} sx={{ ml: '36px' }}>
          <FHCopyToClipboard
            text={form_1500_data?.patient?.dob?.split('-')[1] || ''}
            sx={{ ml: '-0.5em' }}
          />
          <Typography>/</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.patient?.dob?.split('-')[2] || ''}
            sx={{ ml: 0 }}
          />
          <Typography>/</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.patient?.dob?.split('-')[0] || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Typography sx={fieldContentStyle}>
          {form_1500_data?.patient?.sex === 'male' ? 'M' : 'F'}
        </Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>4.</Typography>
          <Typography sx={fieldLabelStyle}>
            Insured’s name (Last , First , Middle initial)
          </Typography>
        </Box>
        <FHCopyToClipboard text={form_1500_data?.insured?.name || ''} sx={fieldCopyContentStyle} />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>5.</Typography>
          <Typography sx={fieldLabelStyle}>Patient’s Address</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.patient?.street_address_line_one || ''}
          sx={fieldCopyContentStyle}
        />
        {form_1500_data?.patient?.street_address_line_two && (
          <FHCopyToClipboard
            text={form_1500_data?.patient?.street_address_line_two || ''}
            sx={fieldCopyContentStyle}
          />
        )}
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>City: </Typography>
          <FHCopyToClipboard
            text={form_1500_data?.patient?.street_address_city || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>State: </Typography>
          <FHCopyToClipboard
            text={form_1500_data?.patient?.street_address_state || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>ZIP code: </Typography>
          <FHCopyToClipboard
            text={form_1500_data?.patient?.street_address_zip || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        {form_1500_data?.patient?.phone && (
          <Box display={'flex'} alignItems={'center'}>
            <Typography sx={fieldContentStyle}>Phone: </Typography>
            <Typography sx={{ ml: '0.5em' }}>(</Typography>
            <FHCopyToClipboard
              text={parsePhoneNumber(form_1500_data?.patient?.phone)?.areaCode || ''}
              sx={{ ml: 0 }}
            />
            <Typography>)</Typography>
            <FHCopyToClipboard
              text={parsePhoneNumber(form_1500_data?.patient?.phone)?.phoneNumber || ''}
              sx={{ ml: 0 }}
            />
          </Box>
        )}
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>6.</Typography>
          <Typography sx={fieldLabelStyle}>Patient relationship to Insured</Typography>
        </Box>
        <Typography sx={fieldContentStyle}>
          {form_1500_data?.patient?.relationship_to_insured || ''}
        </Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>7.</Typography>
          <Typography sx={fieldLabelStyle}>Insured’s Address</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.insured?.street_address_line_one || ''}
          sx={fieldCopyContentStyle}
        />
        {form_1500_data?.insured?.street_address_line_two && (
          <FHCopyToClipboard
            text={form_1500_data?.insured?.street_address_line_two || ''}
            sx={fieldCopyContentStyle}
          />
        )}
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>City: </Typography>
          <FHCopyToClipboard
            text={form_1500_data?.insured?.street_address_city || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>State: </Typography>
          <FHCopyToClipboard
            text={form_1500_data?.insured?.street_address_state || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>ZIP code: </Typography>
          <FHCopyToClipboard
            text={form_1500_data?.insured?.street_address_zip || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        {form_1500_data?.insured?.phone && (
          <Box display={'flex'} alignItems={'center'}>
            <Typography sx={fieldContentStyle}>Phone: </Typography>
            <Typography ml={'0.5em'}>(</Typography>
            <FHCopyToClipboard
              text={parsePhoneNumber(form_1500_data?.insured?.phone)?.areaCode || ''}
              sx={{ ml: 0 }}
            />
            <Typography>)</Typography>
            <FHCopyToClipboard
              text={parsePhoneNumber(form_1500_data?.insured?.phone)?.phoneNumber || ''}
              sx={{ ml: 0 }}
            />
          </Box>
        )}
      </Box>
      {form_1500_data?.other_insured && (
        <>
          <Box sx={fieldContainerStyle}>
            <Box display={'flex'}>
              <Typography sx={fieldLabelIndexStyle}>9.</Typography>
              <Typography sx={fieldLabelStyle}>
                Other insured’s name (Last , First , Middle initial)
              </Typography>
            </Box>
            <FHCopyToClipboard
              text={form_1500_data?.other_insured?.name || ''}
              sx={fieldCopyContentStyle}
            ></FHCopyToClipboard>
          </Box>
          <Box sx={fieldContainerStyle}>
            <Box display={'flex'}>
              <Typography sx={fieldLabelIndexStyle}>9d.</Typography>
              <Typography sx={fieldLabelStyle}>Insurance plan name or program name</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <FHCopyToClipboard
                text={`${form_1500_data?.other_insured?.insurance_plan_name}` || ''}
                sx={fieldCopyContentStyle}
              ></FHCopyToClipboard>
              <Typography sx={{ mx: 1 }}> - </Typography>
              <FHCopyToClipboard
                text={` ${form_1500_data?.other_insured?.member_id}`}
              ></FHCopyToClipboard>
            </Box>
          </Box>
        </>
      )}
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>10.</Typography>
          <Typography sx={fieldLabelStyle}>Is patient’s condition related to</Typography>
        </Box>
        <Typography sx={fieldContentStyle}>
          a. {form_1500_data?.related_patient_conditions?.employment || ''}
        </Typography>
        <Typography sx={fieldContentStyle}>
          b. {form_1500_data?.related_patient_conditions?.auto_accident || ''}
        </Typography>
        <Typography sx={fieldContentStyle}>
          c. {form_1500_data?.related_patient_conditions?.other_accident || ''}
        </Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>11a.</Typography>
          <Typography sx={fieldLabelStyle}>Insured’s date of birth and sex</Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} sx={{ ml: '36px' }}>
          <FHCopyToClipboard
            text={form_1500_data?.insured?.dob?.split('-')[1] || ''}
            sx={{ ml: '-0.5em' }}
          />
          <Typography>/</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.insured?.dob?.split('-')[2] || ''}
            sx={{ ml: 0 }}
          />
          <Typography>/</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.insured?.dob?.split('-')[0] || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Typography sx={fieldContentStyle}>
          {form_1500_data?.insured?.sex === 'male' ? 'M' : 'F'}
        </Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>11d.</Typography>
          <Typography sx={fieldLabelStyle}>Is there another health benefit plan</Typography>
        </Box>
        <Typography sx={fieldContentStyle}>
          {form_1500_data?.is_there_another_health_benefit_plan ? 'Yes' : 'No'}
        </Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>19d.</Typography>
          <Typography sx={fieldLabelStyle}>Additional claimData information</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.additional_information || ''}
          sx={fieldCopyContentStyle}
        />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>21.</Typography>
          <Typography sx={fieldLabelStyle}>
            Diagnosis or nature of illness or injury related A-L to service line below (24E)
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography sx={fieldContentStyle}>a. </Typography>
          <FHCopyToClipboard
            text={
              form_1500_data?.diagnosis_or_nature_of_illness_or_injury_related_al_service_lines ||
              ''
            }
            sx={{ ml: 0 }}
          />
        </Box>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>22.</Typography>
          <Typography sx={fieldLabelStyle}>Resubmission code</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.resubmission_code || ''}
          sx={fieldCopyContentStyle}
        />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>23.</Typography>
          <Typography sx={fieldLabelStyle}>QC</Typography>
        </Box>
        <FHCopyToClipboard text={form_1500_data?.qc || ''} sx={fieldCopyContentStyle} />
        {form_1500_data?.prior_authorization_number && (
          <FHCopyToClipboard
            text={form_1500_data?.prior_authorization_number || ''}
            sx={fieldCopyContentStyle}
          />
        )}
      </Box>

      <Claim1500FormServiceLines serviceLines={form_1500_data?.service_lines || []} />

      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>25.</Typography>
          <Typography sx={fieldLabelStyle}>Federal tax I.D number</Typography>
        </Box>
        <FHCopyToClipboard text={form_1500_data?.federal_tax_id || ''} sx={fieldCopyContentStyle} />
        <Typography sx={fieldContentStyle}>EIN</Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>26.</Typography>
          <Typography sx={fieldLabelStyle}>Patient’s account NO.</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.patient.account_number || ''}
          sx={fieldCopyContentStyle}
        />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>27.</Typography>
          <Typography sx={fieldLabelStyle}>Accept assignment</Typography>
        </Box>
        <Typography sx={fieldContentStyle}>{form_1500_data?.accept_assignment || ''}</Typography>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>28.</Typography>
          <Typography sx={fieldLabelStyle}>Total charge</Typography>
        </Box>
        <FHCopyToClipboard
          text={usDollarFormatter.format(form_1500_data?.total_charge || 0) || ''}
          sx={fieldCopyContentStyle}
        />
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>32.</Typography>
          <Typography sx={fieldLabelStyle}>Service facility location information</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.service_facility_address?.name || ''}
          sx={fieldCopyContentStyle}
        />
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>Address line one:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.service_facility_address?.street_address_line_one || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        {form_1500_data?.service_facility_address?.street_address_line_two && (
          <Box display={'flex'} sx={fieldContentStyle}>
            <Typography>Address line two:</Typography>
            <FHCopyToClipboard
              text={form_1500_data?.service_facility_address?.street_address_line_two || ''}
              sx={{ ml: 0 }}
            />
          </Box>
        )}
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>City:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.service_facility_address?.street_address_city || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>State:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.service_facility_address?.street_address_state || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>ZIP code:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.service_facility_address?.street_address_zip || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>NPI:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.service_facility_address?.npi || ''}
            sx={{ ml: 0 }}
          />
        </Box>
      </Box>
      <Box sx={fieldContainerStyle}>
        <Box display={'flex'}>
          <Typography sx={fieldLabelIndexStyle}>33.</Typography>
          <Typography sx={fieldLabelStyle}>Billing provider Info & PH</Typography>
        </Box>
        <FHCopyToClipboard
          text={form_1500_data?.billing_provider_info?.organization || ''}
          sx={fieldCopyContentStyle}
        />
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>Address line one:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.billing_provider_info?.address_line_one || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        {form_1500_data?.billing_provider_info?.address_line_two && (
          <Box display={'flex'} sx={fieldContentStyle}>
            <Typography>Address line two:</Typography>
            <FHCopyToClipboard
              text={form_1500_data?.billing_provider_info?.address_line_two || ''}
              sx={{ ml: 0 }}
            />
          </Box>
        )}
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>City:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.billing_provider_info?.city || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>State:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.billing_provider_info?.state || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>ZIP code:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.billing_provider_info?.zip || ''}
            sx={{ ml: 0 }}
          />
        </Box>
        <Box display={'flex'} sx={fieldContentStyle}>
          <Typography>NPI:</Typography>
          <FHCopyToClipboard
            text={form_1500_data?.billing_provider_info?.npi || ''}
            sx={{ ml: 0 }}
          />
        </Box>
      </Box>
    </>
  );
}

export default Claim1500FormInfo;
